import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Alert,
  Pressable,
  ScrollView,
  AppState,
  Platform,
} from 'react-native';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import { useTranslation } from 'react-i18next';
import * as Haptics from 'expo-haptics';
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { useDb } from '../../db/client';
import { getProject } from '../../features/projects/projectRepository';
import {
  ensurePrimaryCounter,
  listCounters,
  getCounterValue,
  tapCounter,
  canDecrement,
  createExtraCounter,
  renameCounter,
  deleteExtraCounter,
  MAX_EXTRA_COUNTERS,
  ExtraCounterLimitError,
} from '../../features/session/counters';
import {
  getOpenTimeEntry,
  listClosedTimeEntries,
  getLastCounterActivityAt,
  checkImplausibleOpenSession,
  startTimeEntry,
  pauseTimeEntry,
  adjustImplausibleSession,
} from '../../features/session/timeEntries';
import { calculateElapsedSeconds, formatDurationHms, type ClosedSession } from '../../domain/timer';
import { schedulePauseReminder, cancelPauseReminder } from '../../features/session/pauseNotification';
import {
  dimScreenBrightness,
  restoreScreenBrightness,
  restorePendingBrightnessIfDirty,
  SESSION_DIM_TIMEOUT_MS,
  SESSION_KEEP_AWAKE_TAG,
} from '../../features/session/screenEnergy';
import {
  getPreferences,
  DEFAULT_PREFERENCES,
  type Preferences,
} from '../../features/preferences/preferencesStore';
import type { Project, Counter, TimeEntry } from '../../db/schema';
import { colors, fonts, radii, spacing } from '../theme';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';
import { Chip } from '../shared/Chip';

/** Preset "razoável" (spec S03: "não precisa de um seletor de hora completo")
 * oferecido no diálogo de sessão implausível: encerra a sessão alguns
 * minutos depois do último sinal de atividade conhecido (último toque de
 * contador, ou o início da sessão se nenhum toque aconteceu), em vez de
 * contar a duração implausível inteira contra o projeto. */
const IMPLAUSIBLE_PRESET_GRACE_MINUTES = 5;

function toClosedSession(entry: TimeEntry): ClosedSession | null {
  if (!entry.endedAt) return null;
  return { startedAt: entry.startedAt, endedAt: entry.endedAt };
}

/**
 * Tela de Sessão (Etapa 12 — spec S03): a tela mais usada do app. Regras
 * invioláveis que este componente PRECISA respeitar (ver brief/spec para o
 * detalhe de cada uma):
 *  - Cronômetro NUNCA contado em memória — `elapsedSeconds` é sempre
 *    recalculado por `calculateElapsedSeconds` (domain/timer.ts) a partir de
 *    `openEntry.startedAt` + `closedSessions` lidos do banco; o
 *    `setInterval` de 1s só força um re-render mudando `now`, nunca
 *    incrementa um contador próprio.
 *  - Cada toque de contador grava um `counter_event` (append-only) via
 *    `tapCounter` — o valor exibido é materializado no cliente a partir do
 *    retorno dela, nunca um estado incrementado independente do banco.
 *  - Preço não aparece aqui (fora de escopo, spec S04/Etapa 13).
 */
export function SessionScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const navigation = useNavigation();
  const { projectId: rawProjectId } = useLocalSearchParams<{ projectId: string }>();
  const projectId = Array.isArray(rawProjectId) ? rawProjectId[0] : rawProjectId;
  const { user } = useAuth();
  const { db, ready } = useDb();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [primaryCounter, setPrimaryCounter] = useState<Counter | null>(null);
  const [extraCounters, setExtraCounters] = useState<Counter[]>([]);
  const [counterValues, setCounterValues] = useState<Record<string, number>>({});
  const [activeCounterId, setActiveCounterId] = useState<string | null>(null);

  const [openEntry, setOpenEntry] = useState<TimeEntry | null>(null);
  const [closedSessions, setClosedSessions] = useState<ClosedSession[]>([]);
  const [now, setNow] = useState<Date>(new Date());

  const [showAddForm, setShowAddForm] = useState(false);
  const [newCounterName, setNewCounterName] = useState('');

  const [managingCounterId, setManagingCounterId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const running = openEntry !== null;

  // `counterValuesRef` espelha `counterValues` SINCRONAMENTE (mutado direto
  // em `handleTap`, não só via effect) — necessário porque dois toques
  // muito rápidos no mesmo contador (o caso comum: "somar 20 carreiras
  // olhando pra outro lugar") podem acontecer antes do primeiro re-render
  // commitar o novo valor em `counterValues`; ler do estado React nesse
  // meio-tempo pegaria um valor desatualizado (perderia um toque/duplicaria
  // um decremento bloqueado). `counterValuesRef` é sempre a fonte de
  // verdade "otimista" mais recente; `counterValues` (estado) só existe pra
  // disparar o re-render.
  const counterValuesRef = useRef<Record<string, number>>({});
  const runningRef = useRef(false);
  const dimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originalBrightnessRef = useRef<number | null>(null);
  const isDimmedRef = useRef(false);

  // Preferências (Etapa 14): defaults preservam o comportamento das etapas
  // anteriores (keep-awake/brilho reduzido sempre ligados) até a leitura
  // real do AsyncStorage terminar. `prefsRef` espelha `prefs` para uso
  // dentro de callbacks estáveis (`resetInactivity`/`scheduleDim`), mesmo
  // raciocínio de `runningRef`/`counterValuesRef` acima.
  const [prefs, setPrefs] = useState<Preferences>(DEFAULT_PREFERENCES);
  const prefsRef = useRef<Preferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    getPreferences()
      .then((loaded) => {
        prefsRef.current = loaded;
        setPrefs(loaded);
      })
      .catch((error) => {
        console.error('SessionScreen: falha ao carregar preferências', error);
      });
  }, []);

  const load = useCallback(async () => {
    if (!ready || !db || !user || !projectId) return;
    setLoading(true);
    try {
      const loadedProject = await getProject(db, projectId);
      if (!loadedProject) {
        setNotFound(true);
        return;
      }
      setProject(loadedProject);

      const primary = await ensurePrimaryCounter(db, projectId);
      const all = await listCounters(db, projectId);
      const extras = all.filter((counter) => !counter.isPrimary);
      setPrimaryCounter(primary);
      setExtraCounters(extras);
      setActiveCounterId((current) => current ?? primary.id);

      const values: Record<string, number> = {};
      for (const counter of all) {
        values[counter.id] = await getCounterValue(db, counter.id);
      }
      counterValuesRef.current = values;
      setCounterValues(values);

      const [open, closed] = await Promise.all([
        getOpenTimeEntry(db, projectId),
        listClosedTimeEntries(db, projectId),
      ]);
      setOpenEntry(open ?? null);
      setClosedSessions(closed.map(toClosedSession).filter((session): session is ClosedSession => session !== null));
      setNow(new Date());
    } catch (error) {
      console.error('SessionScreen: falha ao carregar sessão', error);
      Alert.alert(t('session.errorTitle'), t('session.genericErrorMessage'));
    } finally {
      setLoading(false);
    }
  }, [ready, db, user, projectId, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Brilho "sujo" deixado por uma visita ANTERIOR a esta tela que não
  // conseguiu se restaurar sozinha (app morto com o brilho ainda reduzido)
  // — checagem defensiva também aqui, além do boot do app (app/_layout.tsx),
  // conforme a spec S03 pede.
  useEffect(() => {
    restorePendingBrightnessIfDirty().catch((error) => {
      console.error('SessionScreen: falha ao restaurar brilho pendente', error);
    });
  }, []);

  // Diálogo de sessão implausível (spec S03: "> 4h sem toque, ao abrir a
  // tela") — roda só quando uma sessão aberta é detectada (não a cada tick
  // do relógio).
  useEffect(() => {
    if (!db || !projectId || !openEntry) return;
    let cancelled = false;

    (async () => {
      const check = await checkImplausibleOpenSession(db, projectId, new Date());
      if (cancelled || !check?.implausible) return;
      const lastActivityAt = await getLastCounterActivityAt(db, projectId, check.timeEntry.startedAt);
      if (cancelled) return;

      const presetEndedAt = new Date(
        (lastActivityAt ?? check.timeEntry.startedAt).getTime() + IMPLAUSIBLE_PRESET_GRACE_MINUTES * 60_000
      );

      Alert.alert(
        t('session.implausibleTitle'),
        t('session.implausibleMessage'),
        [
          {
            text: t('session.implausibleEndNow'),
            onPress: () => resolveImplausibleSession(check.timeEntry.id, new Date()),
          },
          {
            text: t('session.implausibleEndPreset', { minutes: IMPLAUSIBLE_PRESET_GRACE_MINUTES }),
            onPress: () => resolveImplausibleSession(check.timeEntry.id, presetEndedAt),
          },
        ],
        { cancelable: false }
      );
    })();

    return () => {
      cancelled = true;
    };
    // NB: propositalmente dependente só de `openEntry?.id` (não de `t`,
    // que muda de identidade a cada render) — este efeito deve rodar de
    // novo apenas quando uma NOVA sessão aberta é detectada, não a cada
    // re-render por outro motivo.
  }, [db, projectId, openEntry?.id]);

  const resolveImplausibleSession = async (timeEntryId: string, endedAt: Date) => {
    if (!db) return;
    try {
      await adjustImplausibleSession(db, timeEntryId, endedAt);
      await cancelPauseReminder();
      await load();
    } catch (error) {
      console.error('SessionScreen: falha ao ajustar sessão implausível', error);
      Alert.alert(t('session.errorTitle'), t('session.genericErrorMessage'));
    }
  };

  // Cronômetro: `setInterval` só atualiza `now` (força re-render) — o valor
  // exibido é sempre recalculado por `calculateElapsedSeconds` a partir de
  // `openEntry.startedAt`/`closedSessions` (lidos do banco), nunca de um
  // contador próprio incrementado aqui.
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [running]);

  const elapsedSeconds = useMemo(
    () =>
      calculateElapsedSeconds({
        openSessionStartedAt: openEntry ? openEntry.startedAt : null,
        closedSessions,
        now,
      }),
    [openEntry, closedSessions, now]
  );

  // Keep-awake: só nesta tela, só com o cronômetro rodando (spec S03) e só
  // se a preferência "manter tela ligada" estiver ativa (Etapa 14).
  useEffect(() => {
    if (running && prefs.keepScreenOnEnabled) {
      activateKeepAwakeAsync(SESSION_KEEP_AWAKE_TAG).catch((error) => {
        console.error('SessionScreen: falha ao ativar keep-awake', error);
      });
    } else {
      deactivateKeepAwake(SESSION_KEEP_AWAKE_TAG).catch(() => {});
    }
  }, [running, prefs.keepScreenOnEnabled]);

  const restoreBrightnessIfDimmed = useCallback(() => {
    if (isDimmedRef.current && originalBrightnessRef.current !== null) {
      restoreScreenBrightness(originalBrightnessRef.current).catch((error) => {
        console.error('SessionScreen: falha ao restaurar brilho', error);
      });
      isDimmedRef.current = false;
      originalBrightnessRef.current = null;
    }
  }, []);

  const scheduleDim = useCallback(() => {
    if (dimTimerRef.current) clearTimeout(dimTimerRef.current);
    dimTimerRef.current = setTimeout(() => {
      if (!runningRef.current) return;
      dimScreenBrightness()
        .then((original) => {
          originalBrightnessRef.current = original;
          isDimmedRef.current = true;
        })
        .catch((error) => {
          console.error('SessionScreen: falha ao reduzir brilho', error);
        });
    }, SESSION_DIM_TIMEOUT_MS);
  }, []);

  // Brilho reduzido (spec S03): só agenda a redução enquanto o cronômetro
  // roda E a preferência "reduzir brilho" está ativa (Etapa 14); ao pausar
  // ou desligar a preferência, cancela o temporizador pendente e restaura
  // na hora se já estava reduzido.
  useEffect(() => {
    runningRef.current = running;
    prefsRef.current = prefs;
    if (running && prefs.reduceBrightnessEnabled) {
      scheduleDim();
    } else {
      if (dimTimerRef.current) {
        clearTimeout(dimTimerRef.current);
        dimTimerRef.current = null;
      }
      restoreBrightnessIfDimmed();
    }
  }, [running, prefs, scheduleDim, restoreBrightnessIfDimmed]);

  // Qualquer toque na tela restaura o brilho e reinicia o temporizador de
  // 30s (spec S03), se a preferência estiver ativa. Chamado explicitamente
  // por cada handler de interação (toques de contador, iniciar/pausar,
  // trocar contador) — cobre o padrão de uso real desta tela (a artesã
  // interage tocando os botões, não tocando em áreas vazias) — e também
  // pelo Pressable de fundo do container (abaixo), que cobre toques fora de
  // qualquer botão.
  const resetInactivity = useCallback(() => {
    restoreBrightnessIfDimmed();
    if (runningRef.current && prefsRef.current.reduceBrightnessEnabled) {
      scheduleDim();
    }
  }, [restoreBrightnessIfDimmed, scheduleDim]);

  // Restaurar brilho original SEMPRE ao sair da tela ou ir para background
  // (spec S03) — cobre navegação normal (desmontagem) e AppState mudando
  // para background/inactive enquanto a tela ainda está montada. Kill do
  // app é coberto pela flag em AsyncStorage (brightnessFlag.ts) + a
  // restauração defensiva no próximo boot/montagem, já que nenhum código
  // roda durante um kill de verdade.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state !== 'active') {
        restoreBrightnessIfDimmed();
      }
    });
    return () => subscription.remove();
  }, [restoreBrightnessIfDimmed]);

  useEffect(() => {
    return () => {
      if (dimTimerRef.current) clearTimeout(dimTimerRef.current);
      restoreBrightnessIfDimmed();
      deactivateKeepAwake(SESSION_KEEP_AWAKE_TAG).catch(() => {});
    };
    // NB: propositalmente `[]` (roda só no unmount da tela) — usa a versão
    // mais recente de `restoreBrightnessIfDimmed` via closure porque essa
    // função só depende de refs (estáveis), não de props/state que mudem.
  }, []);

  // Proteção contra fechamento acidental de aba/recarregamento no navegador
  // enquanto a sessão está em andamento.
  useEffect(() => {
    if (Platform.OS !== 'web' || !running) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [running]);

  // Sem navegação acidental (spec S03): voltar (gesto ou botão) com o
  // cronômetro rodando pede confirmação. O botão "Voltar" visível abaixo
  // chama `router.back()`, que passa pelo mesmo navigator interceptado
  // aqui — um único ponto cobre gesto, botão físico e o botão da tela.
  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (event) => {
      if (!runningRef.current) return;
      event.preventDefault();
      Alert.alert(t('session.exitConfirmTitle'), t('session.exitConfirmMessage'), [
        { text: t('session.exitConfirmContinue'), style: 'cancel' },
        {
          text: t('session.exitConfirmPauseAndExit'),
          style: 'destructive',
          onPress: async () => {
            if (db && openEntry) {
              try {
                await pauseTimeEntry(db, openEntry.id);
                await cancelPauseReminder();
              } catch (error) {
                console.error('SessionScreen: falha ao pausar ao sair', error);
              }
            }
            navigation.dispatch(event.data.action);
          },
        },
      ]);
    });
    return unsubscribe;
  }, [navigation, db, openEntry, t]);

  const handleStartPause = async () => {
    if (!db || !project) return;
    resetInactivity();
    try {
      if (openEntry) {
        const closedEntry = await pauseTimeEntry(db, openEntry.id);
        const closed = toClosedSession(closedEntry);
        if (closed) setClosedSessions((prev) => [...prev, closed]);
        setOpenEntry(null);
        setNow(new Date());
        await cancelPauseReminder();
      } else {
        const entry = await startTimeEntry(db, project.id);
        setOpenEntry(entry);
        setNow(new Date());
        await schedulePauseReminder();
      }
    } catch (error) {
      console.error('SessionScreen: falha ao iniciar/pausar cronômetro', error);
      Alert.alert(t('session.errorTitle'), t('session.genericErrorMessage'));
    }
  };

  const handleTap = (counterId: string, delta: 1 | -1) => {
    if (!db) return;
    resetInactivity();

    const currentValue = counterValuesRef.current[counterId] ?? 0;
    if (delta === -1 && !canDecrement(currentValue)) return;

    (delta === 1
      ? Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium)
      : Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning)
    ).catch(() => {});

    const optimisticValue = currentValue + delta;
    counterValuesRef.current = { ...counterValuesRef.current, [counterId]: optimisticValue };
    setCounterValues(counterValuesRef.current);

    tapCounter(db, counterId, delta, currentValue).catch((error) => {
      console.error('SessionScreen: falha ao registrar toque de contador', error);
      counterValuesRef.current = { ...counterValuesRef.current, [counterId]: currentValue };
      setCounterValues(counterValuesRef.current);
      Alert.alert(t('session.errorTitle'), t('session.genericErrorMessage'));
    });
  };

  const handleSelectCounter = (counterId: string) => {
    resetInactivity();
    setActiveCounterId(counterId);
  };

  const handleAddCounter = async () => {
    if (!db || !project) return;
    const trimmed = newCounterName.trim();
    if (!trimmed) {
      Alert.alert(t('session.counters.addErrorTitle'), t('session.counters.nameRequiredMessage'));
      return;
    }
    try {
      const created = await createExtraCounter(db, project.id, trimmed);
      setExtraCounters((prev) => [...prev, created]);
      counterValuesRef.current = { ...counterValuesRef.current, [created.id]: 0 };
      setCounterValues(counterValuesRef.current);
      setActiveCounterId(created.id);
      setNewCounterName('');
      setShowAddForm(false);
    } catch (error) {
      if (error instanceof ExtraCounterLimitError) {
        Alert.alert(t('session.counters.limitTitle'), t('session.counters.limitMessage', { limit: MAX_EXTRA_COUNTERS }));
      } else {
        console.error('SessionScreen: falha ao criar contador extra', error);
        Alert.alert(t('session.errorTitle'), t('session.genericErrorMessage'));
      }
    }
  };

  const managingCounter = extraCounters.find((counter) => counter.id === managingCounterId) ?? null;

  const handleLongPressCounter = (counter: Counter) => {
    if (counter.isPrimary) return;
    resetInactivity();
    setManagingCounterId(counter.id);
    setRenameValue(counter.name);
  };

  const handleConfirmRename = async () => {
    if (!db || !managingCounter) return;
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    try {
      const updated = await renameCounter(db, managingCounter.id, trimmed);
      setExtraCounters((prev) => prev.map((counter) => (counter.id === updated.id ? updated : counter)));
      setManagingCounterId(null);
    } catch (error) {
      console.error('SessionScreen: falha ao renomear contador', error);
      Alert.alert(t('session.errorTitle'), t('session.genericErrorMessage'));
    }
  };

  const handleDeleteManagedCounter = () => {
    if (!db || !managingCounter) return;
    const counter = managingCounter;
    Alert.alert(t('session.counters.deleteConfirmTitle'), t('session.counters.deleteConfirmMessage'), [
      { text: t('session.counters.deleteConfirmCancel'), style: 'cancel' },
      {
        text: t('session.counters.deleteConfirmConfirm'),
        style: 'destructive',
        onPress: async () => {
          try {
            await deleteExtraCounter(db, counter);
            setExtraCounters((prev) => prev.filter((c) => c.id !== counter.id));
            setActiveCounterId((current) => (current === counter.id ? primaryCounter?.id ?? null : current));
            setManagingCounterId(null);
          } catch (error) {
            console.error('SessionScreen: falha ao excluir contador', error);
            Alert.alert(t('session.errorTitle'), t('session.genericErrorMessage'));
          }
        },
      },
    ]);
  };

  if (!ready || loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.sageDeep} />
        <Text style={styles.muted}>{t('session.loading')}</Text>
      </View>
    );
  }

  if (notFound || !project || !primaryCounter) {
    return (
      <View style={styles.centered}>
        <Text style={styles.muted}>{t('session.notFound')}</Text>
        <Button title={t('session.back')} onPress={() => router.back()} />
      </View>
    );
  }

  const allCounters = [primaryCounter, ...extraCounters];
  const activeCounter = allCounters.find((counter) => counter.id === activeCounterId) ?? primaryCounter;
  const activeValue = counterValues[activeCounter.id] ?? 0;
  const canAddMoreCounters = extraCounters.length < MAX_EXTRA_COUNTERS;

  return (
    <Pressable style={styles.container} onPress={resetInactivity}>
      <View style={styles.headerRow}>
        <Button title={`< ${t('session.back')}`} variant="ghost" onPress={() => router.back()} />
        <Text style={styles.timer}>{formatDurationHms(elapsedSeconds)}</Text>
      </View>

      <Text style={styles.projectName} numberOfLines={1} ellipsizeMode="tail">
        {project.name}
      </Text>

      <Pressable style={[styles.startPauseButton, running && styles.pauseButtonActive]} onPress={handleStartPause}>
        <Text style={styles.startPauseText}>{t(running ? 'session.pauseButton' : 'session.startButton')}</Text>
      </Pressable>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsRow} contentContainerStyle={styles.chipsContent}>
        {allCounters.map((counter) => (
          <Chip
            key={counter.id}
            label={`${counter.name} · ${counterValues[counter.id] ?? 0}`}
            active={activeCounter.id === counter.id}
            onPress={() => handleSelectCounter(counter.id)}
            onLongPress={() => handleLongPressCounter(counter)}
          />
        ))}

        {canAddMoreCounters && (
          <Chip label={t('session.counters.addButton')} dashed onPress={() => setShowAddForm((v) => !v)} />
        )}
      </ScrollView>

      {showAddForm && (
        <View style={styles.addForm}>
          <TextField
            placeholder={t('session.counters.addPlaceholder')}
            value={newCounterName}
            onChangeText={setNewCounterName}
          />
          <View style={styles.addFormButtons}>
            <Button title={t('session.counters.addCancel')} variant="ghost" onPress={() => setShowAddForm(false)} />
            <Button title={t('session.counters.addConfirm')} onPress={handleAddCounter} />
          </View>
        </View>
      )}

      {managingCounter && (
        <View style={styles.manageForm}>
          <Text style={styles.manageTitle}>{t('session.counters.manageTitle', { name: managingCounter.name })}</Text>
          <TextField
            placeholder={t('session.counters.renamePlaceholder')}
            value={renameValue}
            onChangeText={setRenameValue}
          />
          <View style={styles.addFormButtons}>
            <Button title={t('session.counters.cancelAction')} variant="ghost" onPress={() => setManagingCounterId(null)} />
            <Button title={t('session.counters.renameConfirm')} onPress={handleConfirmRename} />
            <Button title={t('session.counters.deleteAction')} variant="danger" onPress={handleDeleteManagedCounter} />
          </View>
        </View>
      )}

      <View style={styles.counterArea}>
        <Text style={styles.counterName}>{activeCounter.name}</Text>
        <Text style={styles.counterValue}>{activeValue}</Text>

        <Pressable
          style={[styles.minusButton, !canDecrement(activeValue) && styles.buttonDisabled]}
          disabled={!canDecrement(activeValue)}
          onPress={() => handleTap(activeCounter.id, -1)}
        >
          <Text style={styles.minusText}>{t('session.decrement')}</Text>
        </Pressable>

        <Pressable style={styles.plusButton} onPress={() => handleTap(activeCounter.id, 1)}>
          <Text style={styles.plusText}>{t('session.increment')}</Text>
        </Pressable>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: spacing.md, backgroundColor: colors.sand },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.md, backgroundColor: colors.sand },
  muted: { fontFamily: fonts.body, color: colors.thread, marginTop: spacing.sm },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  projectName: { fontFamily: fonts.display, fontSize: 20, color: colors.ink, marginTop: spacing.xs },
  timer: { fontFamily: fonts.bodyBold, fontSize: 24, fontVariant: ['tabular-nums'], color: colors.ink },
  startPauseButton: {
    marginTop: spacing.sm,
    backgroundColor: colors.sageDeep,
    borderRadius: radii.sm,
    paddingVertical: 14,
    alignItems: 'center',
  },
  pauseButtonActive: { backgroundColor: colors.clay },
  startPauseText: { fontFamily: fonts.bodyBold, color: colors.onSage, fontSize: 18 },
  chipsRow: { marginTop: spacing.md, maxHeight: 48 },
  chipsContent: { alignItems: 'center' },
  addForm: { marginTop: spacing.sm },
  manageForm: { marginTop: spacing.sm },
  manageTitle: { fontFamily: fonts.bodyBold, fontSize: 14, color: colors.ink, marginBottom: spacing.xs + 2 },
  addFormButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.sm },
  counterArea: { flex: 1, marginTop: spacing.md, alignItems: 'center' },
  counterName: { fontFamily: fonts.body, fontSize: 16, color: colors.inkSoft },
  counterValue: { fontFamily: fonts.bodyBold, fontSize: 72, color: colors.ink, marginVertical: spacing.sm },
  minusButton: {
    width: '40%',
    paddingVertical: 16,
    borderRadius: radii.sm,
    backgroundColor: colors.sandPanel,
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  buttonDisabled: { opacity: 0.4 },
  minusText: { fontFamily: fonts.bodyBold, fontSize: 28, color: colors.inkSoft },
  plusButton: {
    width: '100%',
    flex: 1,
    minHeight: 220,
    borderRadius: radii.lg,
    backgroundColor: colors.sageDeep,
    alignItems: 'center',
    justifyContent: 'center',
  },
  plusText: { fontFamily: fonts.bodyBold, fontSize: 64, color: colors.onSage },
});
